import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  Loader2,
  PackageCheck,
  X,
  XCircle,
} from 'lucide-react';
import { useBackgroundJobs } from '../contexts/BackgroundJobsContext';
import { shipmentApi } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { Button } from './ui/Button';

export function BackgroundJobProgress() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const { jobs, activeJob, updateJob, cancelJob, clearJob } = useBackgroundJobs();
  const visibleJob = activeJob ?? jobs[0] ?? null;
  const batchId = visibleJob?.serverBatchId;
  const lastSettledRef = useRef<{ batchId: string; settled: number } | null>(null);
  const recoveredBatchRef = useRef<string | null>(null);

  const batchQuery = useQuery({
    queryKey: ['shipment-batch', batchId],
    queryFn: async () => {
      const { data } = await shipmentApi.getBatch(batchId!);
      return data.batch;
    },
    enabled: isAuthenticated && Boolean(batchId),
    refetchInterval: visibleJob?.status === 'running' ? 1000 : false,
    staleTime: 0,
  });

  useEffect(() => {
    if (!visibleJob) return;
    const batch = batchQuery.data;
    if (!batch) return;
    updateJob(visibleJob.id, { shipmentBatch: batch });

    const settled = batch.counts.postProcessingCompleted + batch.counts.postProcessingFailed;
    if (
      lastSettledRef.current?.batchId !== batch.id
      || lastSettledRef.current.settled !== settled
    ) {
      lastSettledRef.current = { batchId: batch.id, settled };
      window.dispatchEvent(new CustomEvent('ecomflow:shipment-batch-updated', {
        detail: { batchId: batch.id, settled },
      }));
    }
  }, [batchQuery.data, batchQuery.dataUpdatedAt, updateJob, visibleJob?.id]);

  useEffect(() => {
    const batch = batchQuery.data;
    if (
      !visibleJob?.restored
      || !batch
      || batch.status !== 'CREATING'
      || recoveredBatchRef.current === batch.id
    ) return;

    recoveredBatchRef.current = batch.id;
    void shipmentApi.finishBatch(batch.id).then(({ data }) => {
      updateJob(visibleJob.id, {
        shipmentBatch: data.batch,
        restored: false,
      });
    }).catch(() => {
      recoveredBatchRef.current = null;
    });
  }, [batchQuery.data, batchQuery.dataUpdatedAt, updateJob, visibleJob?.id, visibleJob?.restored]);

  useEffect(() => {
    if (!visibleJob || visibleJob.status === 'running') return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearJob(visibleJob.id);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [clearJob, visibleJob]);

  if (!isAuthenticated || !visibleJob) return null;

  const batch = visibleJob.shipmentBatch;
  const isShipmentBatch = visibleJob.type === 'bulk_shipment' && Boolean(visibleJob.serverBatchId);
  const itemLabel = visibleJob.type === 'product_reviews' ? 'elementów' : 'zamówień';
  const shipmentFailures = batch?.counts.shipmentFailures ?? 0;
  const processingFailures = batch?.counts.postProcessingFailed ?? 0;
  const totalFailures = shipmentFailures + processingFailures;
  const retrying = batch?.retrying ?? [];
  const reportedIssues = [
    ...(batch?.errors ?? []).map(error => ({ ...error, retrying: false })),
    ...retrying.map(error => ({ ...error, stage: 'post_processing' as const, retrying: true })),
  ];
  const displayError = (message: string) => message.replace(/^Automation \S+ failed:\s*/, '');
  const created = batch?.counts.shipmentsCreated ?? visibleJob.completed;
  const ready = batch?.counts.postProcessingCompleted ?? 0;
  const pending = batch
    ? batch.counts.waitingForShipment + batch.counts.postProcessingPending
    : Math.max(0, visibleJob.total - visibleJob.completed - visibleJob.failed);
  // Każde zamówienie ma dwa etapy: utworzenie listu oraz przetworzenie
  // automatyzacji po nadaniu. Liczenie obu etapów w jednym mianowniku
  // zapobiega skokowi lub cofnięciu paska po zakończeniu tworzenia listów.
  const completedWorkUnits = batch
    ? created
      + ready
      + processingFailures
      + (shipmentFailures * 2)
    : visibleJob.completed + visibleJob.failed;
  const totalWorkUnits = batch ? visibleJob.total * 2 : visibleJob.total;
  const progress = totalWorkUnits > 0
    ? Math.min(100, Math.round((completedWorkUnits / totalWorkUnits) * 100))
    : 0;
  const isFinished = visibleJob.status !== 'running';
  const canCancelCreation = visibleJob.status === 'running' && !batch?.creationFinishedAt;

  const statusIcon = visibleJob.status === 'running' && retrying.length === 0
    ? <Loader2 className="h-5 w-5 animate-spin text-accent-primary" />
    : visibleJob.status === 'error'
      ? <XCircle className="h-5 w-5 text-danger" />
      : totalFailures > 0 || retrying.length > 0 || visibleJob.status === 'cancelled'
        ? <AlertTriangle className="h-5 w-5 text-warning" />
        : <CheckCircle className="h-5 w-5 text-success" />;

  const goToShipmentBatch = () => {
    if (!batch) return;
    navigate(`/orders?shipmentBatch=${encodeURIComponent(batch.id)}`);
    clearJob(visibleJob.id);
  };

  if (!isShipmentBatch) {
    return (
      <div className="fixed bottom-4 right-4 z-50 min-w-[350px] max-w-[450px] rounded-xl border border-border bg-surface-raised/95 p-4 shadow-xl backdrop-blur-xl">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {statusIcon}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-text-primary">{visibleJob.title}</div>
              <div className="text-sm text-text-secondary">
                {visibleJob.status === 'running'
                  ? `${visibleJob.completed + visibleJob.failed} / ${visibleJob.total}`
                  : `Zakończono: ${visibleJob.completed} sukces, ${visibleJob.failed} błędów`}
              </div>
            </div>
          </div>
          <button
            onClick={() => visibleJob.status === 'running'
              ? cancelJob(visibleJob.id)
              : clearJob(visibleJob.id)}
            className="rounded-lg p-1.5 text-text-secondary transition-colors hover:bg-surface-muted hover:text-text-primary"
            title={visibleJob.status === 'running' ? 'Zatrzymaj' : 'Zamknij'}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-surface-muted">
          <div className="h-full bg-accent-primary transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  return (
    <section
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 w-[min(470px,calc(100vw-2rem))] rounded-xl border border-border bg-surface-raised/95 p-4 shadow-xl backdrop-blur-xl"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-0.5 shrink-0">{statusIcon}</div>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-text-primary">
              Przygotowywanie zamówień
            </h2>
            <p className="mt-0.5 text-sm text-text-secondary">
              {isFinished
                ? totalFailures > 0
                  ? 'Proces zakończony. Część zamówień wymaga sprawdzenia.'
                  : 'Wszystkie zamówienia są gotowe do dalszej pracy.'
                : retrying.length > 0
                  ? 'Automatyzacja zgłosiła błąd. Trwa oczekiwanie na ponowną próbę.'
                  : 'Trwa obsługa zamówień. Poczekaj na ukończenie procesu.'}
            </p>
          </div>
        </div>
        {canCancelCreation && (
          <button
            onClick={() => cancelJob(visibleJob.id)}
            className="rounded-lg p-1.5 text-text-secondary transition-colors hover:bg-danger/10 hover:text-danger"
            title="Zatrzymaj dalsze nadawanie"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-lg bg-surface-muted/70 px-3 py-2">
          <div className="text-text-secondary">Listy utworzone</div>
          <div className="mt-0.5 font-semibold text-text-primary">{created} / {visibleJob.total}</div>
        </div>
        <div className="rounded-lg bg-surface-muted/70 px-3 py-2">
          <div className="text-text-secondary">Gotowe po nadaniu</div>
          <div className="mt-0.5 font-semibold text-success">{ready}</div>
        </div>
        <div className="rounded-lg bg-surface-muted/70 px-3 py-2">
          <div className="text-text-secondary">W toku</div>
          <div className="mt-0.5 font-semibold text-accent-primary">{pending}</div>
        </div>
        <div className="rounded-lg bg-surface-muted/70 px-3 py-2">
          <div className="text-text-secondary">Błędy</div>
          <div className={`mt-0.5 font-semibold ${totalFailures + retrying.length > 0 ? 'text-danger' : 'text-text-primary'}`}>
            {totalFailures + retrying.length}
          </div>
        </div>
      </div>

      {retrying.length > 0 && (
        <div role="status" className="mt-3 rounded-lg border border-warning-border bg-warning-bg p-3 text-sm">
          <p className="font-medium text-text-primary">{displayError(retrying[0].message)}</p>
          <p className="mt-1 text-xs text-text-secondary">
            Zamówienia oczekujące na ponowienie: {retrying.length}. Utworzone listy przewozowe pozostają zapisane.
          </p>
        </div>
      )}

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-surface-muted">
        <div
          className={`h-full overflow-hidden transition-[width] ease-out ${
            isFinished && totalFailures > 0 ? 'bg-warning' : 'bg-accent-primary'
          }`}
          style={{
            width: `${progress}%`,
            transitionDuration: '1200ms',
          }}
        >
          {!isFinished && <div className="h-full w-full animate-pulse bg-white/25" />}
        </div>
      </div>

      {batchQuery.isError && visibleJob.status === 'running' && (
        <p className="mt-3 text-xs text-warning">
          Nie udało się chwilowo odświeżyć postępu. Ponawiam sprawdzanie automatycznie.
        </p>
      )}

      {batch && batch.destinationStatuses.length > 1 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-text-secondary hover:text-text-primary">
            Statusy docelowe ({batch.destinationStatuses.length})
          </summary>
          <div className="mt-2 space-y-1 rounded-lg bg-surface-muted/50 p-2 text-xs text-text-secondary">
            {batch.destinationStatuses.map(status => (
              <div key={status.id ?? status.name} className="flex justify-between gap-3">
                <span className="truncate">{status.name}</span>
                <span className="shrink-0 font-medium text-text-primary">{status.count}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {reportedIssues.length > 0 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-danger hover:text-danger/80">
            Zobacz zamówienia wymagające sprawdzenia ({reportedIssues.length})
          </summary>
          <div className="mt-2 max-h-36 space-y-1 overflow-y-auto rounded-lg bg-danger/5 p-2 text-xs text-text-secondary">
            {reportedIssues.slice(0, 30).map(error => (
              <div key={`${error.orderId}:${error.stage}`}>
                <span className="font-medium text-text-primary">#{error.orderNumber}</span>: {displayError(error.message)}
                {error.retrying && <span className="text-warning"> (oczekuje na ponowienie)</span>}
              </div>
            ))}
            {reportedIssues.length > 30 && (
              <div className="text-text-muted">...i {reportedIssues.length - 30} więcej</div>
            )}
          </div>
        </details>
      )}

      {isFinished && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {batch && (
            <Button
              size="sm"
              onClick={goToShipmentBatch}
              leftIcon={<PackageCheck />}
            >
              Pokaż zamówienia z tej operacji
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => clearJob(visibleJob.id)}>
            Zamknij
          </Button>
        </div>
      )}

      {!batch && visibleJob.status === 'running' && (
        <div className="mt-3 text-xs text-text-muted">
          Łączenie z postępem partii {visibleJob.total} {itemLabel}...
        </div>
      )}
    </section>
  );
}
